import * as React from 'react';
import { CaretDownIcon, CaretRightIcon } from '@phosphor-icons/react';
import { cn } from '@/utils/utils';
import { DISABLED, FOCUS, OVERLINE } from '@/components/ui/recipes';

interface DisclosureProps {
  /** The header's label. */
  label: React.ReactNode;
  /** A glyph before the label. */
  icon?: React.ReactNode;
  /** What sits at the header's end before the caret: a count, a status. */
  trailing?: React.ReactNode;
  /** Buttons at the header's end, beside the toggle rather than inside it: randomise, pin. */
  actions?: React.ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  disabled?: boolean;
  /** plain: a header in its words, caret first; section: a panel's section in the small overline, caret after (New anchor); row: a settings row, its name, then its value and a caret at its end (a trigger's How close); field: a panel field's label, caret after, its value at the end (a store model's Source). */
  look?: 'plain' | 'section' | 'row' | 'field';
  children: React.ReactNode;
  className?: string;
}

/** Lureo's disclosure: a header that shows and hides the content under it, its caret before the words (right closed, down open). */
export function Disclosure({ label, icon, trailing, actions, open: openProp, defaultOpen = false, onOpenChange, disabled, look = 'plain', children, className }: DisclosureProps) {
  const [openState, setOpenState] = React.useState(defaultOpen);
  const open = openProp ?? openState;
  const id = React.useId();
  const toggle = () => {
    if (openProp === undefined) setOpenState(!open);
    onOpenChange?.(!open);
  };
  return (
    <div className={className}>
      <div className="flex items-center gap-1">
        <button
          type="button"
          aria-expanded={open}
          aria-controls={id}
          disabled={disabled}
          onClick={toggle}
          className={cn(
            'flex min-w-0 items-center rounded-lureo-radius py-1.5 text-left transition-colors',
            look === 'section'
              ? cn(OVERLINE.list, 'gap-1.5 self-start hover:text-lureo-ink')
              : look === 'field'
                ? cn(OVERLINE.field, 'flex-1 gap-1.5 hover:text-lureo-ink')
                : look === 'row'
                ? 'flex-1 gap-2.5 font-lureo-font-ui text-lureo-13 text-lureo-ink-subtle'
                : 'flex-1 gap-2 font-lureo-font-ui text-lureo-13 font-medium text-lureo-ink hover:text-lureo-brand-ink',
            FOCUS,
            DISABLED,
          )}
        >
          {look === 'plain' && <CaretRightIcon className={cn('h-3.5 w-3.5 shrink-0 text-lureo-ink-subtle transition-transform', open && 'rotate-90')} />}
          {icon && <span className="inline-flex shrink-0 text-lureo-ink-subtle [&_svg]:h-4 [&_svg]:w-4">{icon}</span>}
          <span className={cn('min-w-0 truncate', look !== 'section' && look !== 'field' && 'flex-1')}>{label}</span>
          {look === 'field' && <CaretDownIcon className={cn('h-3 w-3 shrink-0 transition-transform', !open && '-rotate-90')} />}
          {trailing && (
            <span className={cn('shrink-0', look === 'field'
              ? 'ml-auto font-lureo-font-ui font-lureo-weight-subheading text-lureo-14 normal-case leading-[1.3] tracking-normal tabular-nums text-lureo-ink'
              : look === 'row' ? 'font-medium tabular-nums text-lureo-ink' : 'text-lureo-12 font-normal text-lureo-ink-subtle')}
            >
              {trailing}
            </span>
          )}
          {look === 'section' && <CaretDownIcon className={cn('h-3.5 w-3.5 shrink-0 transition-transform', !open && '-rotate-90')} />}
          {look === 'row' && <CaretDownIcon className="h-3 w-3 shrink-0 text-lureo-ink-subtle" />}
        </button>
        {actions && <div className={cn('flex shrink-0 items-center', look === 'section' && 'ml-auto')}>{actions}</div>}
      </div>
      {open && (
        <div id={id} className="pt-1">
          {children}
        </div>
      )}
    </div>
  );
}

interface CollapsibleHeadingProps {
  /** The section's title. */
  title: React.ReactNode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** page: a page section's title (Your projects); card: a card's. */
  level?: 'page' | 'card';
  className?: string;
}

/** A section's title that shows and hides the section, which its owner renders (with search or filters beside it); its caret before the words, up while open and down while closed. */
export function CollapsibleHeading({ title, open, onOpenChange, level = 'page', className }: CollapsibleHeadingProps) {
  return (
    <h2 className={className}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => onOpenChange(!open)}
        className={cn('group inline-flex items-center gap-2 rounded-lureo-radius text-left font-lureo-font-ui text-lureo-ink-strong', level === 'page' ? 'text-lureo-24 font-bold leading-8' : 'text-lureo-18 font-semibold leading-7', FOCUS)}
      >
        <CaretDownIcon className={cn('shrink-0 text-lureo-ink transition-transform group-hover:text-lureo-ink-strong', level === 'page' ? 'h-5 w-5' : 'h-4 w-4', open && 'rotate-180')} />
        <span className="case-lureo-title-case tracking-lureo-title-tracking font-style-lureo-title-style">{title}</span>
      </button>
    </h2>
  );
}
